import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { cn } from '@/lib/cn';
import type { Recurso, TipoDeRecurso } from './api';

/* ── Los siete tipos ───────────────────────────────────────────────────── */

/**
 * Icono, palabra y tinta de cada tipo de recurso.
 *
 * El vanilla pintaba cada insignia así:
 *
 *     background:${meta.color}18; color:${meta.color}
 *
 * **El texto en el mismo color que su fondo tintado.** Es el defecto que
 * `test_contraste_tinte_y_tinta.py` documenta seis veces, con otra forma.
 * Medido el 2026-09-16 con el píxel real, sobre el pie de la tarjeta:
 *
 *     enlace 1,94 · podcast 2,46 · infografía 2,47 · manual 2,80
 *     video 3,21 · libro 3,63 · plantilla 4,84       (mínimo AA: 4,5)
 *
 * Seis de siete bajo el mínimo. Y esa prueba no lo veía, porque el color era
 * una variable JavaScript que su expresión regular no puede resolver.
 *
 * Aquí se usan **las siete tintas que ya midió Novedades** el 2026-09-08
 * —`text-<tinta>` sobre `bg-<tinta>/12`, entre 4,75 y 6,15 en los dos temas—.
 * No hace falta ningún token nuevo, y un tipo no puede quedarse sin versión
 * oscura porque ninguno escribe su color a mano.
 *
 * `podcast` está, y tiene su filtro: el vanilla lo declaraba en `TYPE_META`
 * pero no le puso chip, así que un pódcast salía en «Todos» y en ningún filtro.
 */
export const TIPOS: Record<TipoDeRecurso, { icono: string; etiqueta: string; plural: string; clases: string }> = {
  libro: { icono: 'bi-book', etiqueta: 'Libro', plural: 'Libros', clases: 'text-info-ink bg-info-ink/12' },
  manual: { icono: 'bi-journal-text', etiqueta: 'Manual', plural: 'Manuales', clases: 'text-teal-ink bg-teal-ink/12' },
  plantilla: { icono: 'bi-file-earmark-ruled', etiqueta: 'Plantilla', plural: 'Plantillas', clases: 'text-purple-ink bg-purple-ink/12' },
  video: { icono: 'bi-play-circle', etiqueta: 'Video', plural: 'Videos', clases: 'text-danger-ink bg-danger-ink/12' },
  enlace: { icono: 'bi-link-45deg', etiqueta: 'Enlace', plural: 'Enlaces', clases: 'text-warn-ink bg-warn-ink/12' },
  infografia: { icono: 'bi-bar-chart', etiqueta: 'Infografía', plural: 'Infografías', clases: 'text-ok-ink bg-ok-ink/12' },
  podcast: { icono: 'bi-mic', etiqueta: 'Podcast', plural: 'Podcasts', clases: 'text-pink-ink bg-pink-ink/12' },
};

export const ORDEN_DE_TIPOS = Object.keys(TIPOS) as TipoDeRecurso[];

export function metaDeTipo(tipo: string) {
  return TIPOS[tipo as TipoDeRecurso] ?? {
    icono: 'bi-file-earmark', etiqueta: tipo, plural: tipo, clases: 'text-ink-2 bg-sunken',
  };
}

export function InsigniaDeTipo({ tipo }: { tipo: string }) {
  const m = metaDeTipo(tipo);
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-xs px-2 py-0.5 text-1 font-semibold', m.clases)}>
      <i className={cn('bi', m.icono)} aria-hidden="true" />
      {m.etiqueta}
    </span>
  );
}

/* ── Qué clase de archivo es ───────────────────────────────────────────── */

export type ClaseDeArchivo = 'imagen' | 'video' | 'audio' | 'documento';

/**
 * Qué visor le toca a un archivo.
 *
 * Se lee el `response-content-type` que el servidor puso en la URL presignada
 * (`_resource_urls` lo deriva de la extensión con `mimetypes`), y si no está,
 * la extensión. **No se decide por `resource_type`**, y ese era el defecto:
 * el vanilla trataba todo `video` como YouTube —
 *
 *     const isYouTube = url.includes('youtube') || … || type === 'video';
 *
 * — así que un vídeo SUBIDO como archivo acababa en
 * `https://www.youtube.com/embed/?rel=0`, un embed sin id. Medido el
 * 2026-09-16. Y un enlace de YouTube nunca llegaba a esa rama, porque sin
 * archivo la tarjeta ofrece «Abrir»: la rama solo se alcanzaba donde fallaba.
 */
export function claseDeArchivo(url: string): ClaseDeArchivo {
  let tipo = '';
  let ruta = url;
  try {
    const u = new URL(url, location.origin);
    tipo = u.searchParams.get('response-content-type') ?? '';
    ruta = u.pathname;
  } catch { /* URL relativa rara: se decide por la extensión */ }
  if (!tipo) {
    const ext = ruta.split('.').pop()?.toLowerCase() ?? '';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) tipo = 'image/';
    else if (['mp4', 'webm', 'mov'].includes(ext)) tipo = 'video/';
    else if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) tipo = 'audio/';
  }
  if (tipo.startsWith('image/')) return 'imagen';
  if (tipo.startsWith('video/')) return 'video';
  if (tipo.startsWith('audio/')) return 'audio';
  return 'documento';
}

/** Solo se deja pasar a un `href` o `src` lo que empieza por http(s) o `/`.
 *
 *  Es la mitad del cliente de un doble cinturón: el servidor ya rechaza al
 *  guardar cualquier `external_url` que no sea http(s) o que lleve comillas
 *  (`_url_segura`, `content.py:1271`), porque un `javascript:` en un href se
 *  ejecuta con solo pulsarlo. Esta mitad cubre lo que ya estuviera guardado
 *  antes de esa guarda. */
export function urlSegura(url?: string | null): string | null {
  const u = (url ?? '').trim();
  return /^(https?:\/\/|\/)/i.test(u) ? u : null;
}

/* ── La tarjeta ────────────────────────────────────────────────────────── */

export function TarjetaDeRecurso({ r, onVer }: { r: Recurso; onVer: (r: Recurso) => void }) {
  const m = metaDeTipo(r.resource_type);
  const miniatura = urlSegura(r.preview_url);
  const archivo = urlSegura(r.file_url);
  const descarga = urlSegura(r.download_url) ?? archivo;
  const enlace = urlSegura(r.external_url);

  return (
    <Card className="flex w-full flex-col overflow-hidden">
      <div className="flex h-40 shrink-0 items-center justify-center overflow-hidden border-b border-line bg-sunken">
        {miniatura ? (
          // `alt=""`: la miniatura repite lo que dice el título de debajo, y
          // leerlo dos veces solo alarga la lectura.
          <img src={miniatura} alt="" loading="lazy" className="size-full object-cover" />
        ) : (
          <i className={cn('bi text-9 text-muted opacity-60', m.icono)} aria-hidden="true" />
        )}
      </div>

      <div className="flex flex-1 flex-col gap-1.5 p-4">
        <h3 className="text-4 font-bold leading-snug text-brand-ink">{r.title}</h3>
        {r.description && (
          // `wrap-anywhere`: el vanilla dejaba `overflow-wrap: normal`, y una
          // palabra sin espacios —una URL pegada, por ejemplo— se cortaba sin
          // avisar. Medido: 637 px de contenido en 277 visibles.
          <p className="line-clamp-3 text-2 leading-relaxed text-muted [overflow-wrap:anywhere]">
            {r.description}
          </p>
        )}
      </div>

      {/* El pie va SIN fondo gris, y no es estética: las insignias de tipo son
          texto de 10,5 px con tinte al 12 %, y sobre `bg-sunken` las siete
          bajaban a 4,25–4,33 —bajo el 4,5 del AA—. Medido el 2026-09-16 con el
          píxel real, y verificado a mano con «Libro» (4,36). La primera versión
          copió el `--gray-50` del vanilla sin volver a medir. Sobre la
          superficie de la tarjeta dan lo mismo que en Novedades; el filete de
          arriba ya separa el pie, que es lo que pide una superficie plana. */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-line px-4 py-3">
        <InsigniaDeTipo tipo={r.resource_type} />
        <div className="flex gap-1.5">
          {archivo ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                iconOnly
                onClick={() => onVer(r)}
                aria-label={`Ver «${r.title}»`}
                title="Ver"
              >
                <i className="bi bi-eye" aria-hidden="true" />
              </Button>
              {descarga && (
                <a
                  href={descarga}
                  download={r.title}
                  aria-label={`Descargar «${r.title}»`}
                  title="Descargar"
                  className="inline-flex aspect-square min-h-8 items-center justify-center rounded-sm
                             bg-brand text-on-brand hover:bg-brand-strong focus-visible:focus-anillo
                             pointer-coarse:min-h-11"
                >
                  <i className="bi bi-download" aria-hidden="true" />
                </a>
              )}
            </>
          ) : enlace ? (
            <a
              href={enlace}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-8 items-center gap-1.5 rounded-sm bg-brand px-3 text-2
                         font-semibold text-on-brand hover:bg-brand-strong focus-visible:focus-anillo
                         pointer-coarse:min-h-11"
            >
              <i className="bi bi-box-arrow-up-right" aria-hidden="true" /> Abrir
              <span className="sr-only">(se abre en otra pestaña)</span>
            </a>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
